import {
  ChamberPerksAppAdminConsolePagePermissionsEnum,
  UserType,
} from "../graphql/types";
import type {
  IndividualRoute,
  IndividualRouteGroup,
} from "../enumStrings";

type ChamberAdminRoute = IndividualRoute<UserType.ChamberAdmin>;
type ChamberAdminRouteGroup = IndividualRouteGroup<UserType.ChamberAdmin>;

export type CmpRouteAccess = {
  userType: UserType | null;
  chamberMemberProPermissions: string[];
  chamberPerksDashboardPermissions: string[];
  hasWebsiteBuilderEntitlement: boolean;
  /** The chamber bought the Chamber Perks App™ (CHAMBER_PERKS_APP purchase). */
  hasChamberPerksAppEntitlement: boolean;
};

export const isCmpRouteAllowed = ({
  route,
  access,
}: {
  route: ChamberAdminRoute;
  access: CmpRouteAccess;
}): boolean => {
  // Website Builder entitlement is chamber-level product access, independent
  // of the human user's permission. Owners bypass stored user leaves but never
  // bypass the chamber entitlement.
  if (
    route.requiresWebsiteBuilderEntitlement &&
    !access.hasWebsiteBuilderEntitlement
  ) {
    return false;
  }

  // Chamber Perks App™ Dashboard sign-in refuses chambers without the CPA
  // purchase, so the link is hidden for owners and staff alike.
  if (
    route.requiresChamberPerksAppEntitlement &&
    !access.hasChamberPerksAppEntitlement
  ) {
    return false;
  }

  // CHAMBER_ADMIN is the canonical chamber owner, not a permission-managed
  // staff account. Keep this bypass ahead of human permission metadata so
  // owners automatically receive new dashboard routes without stored-leaf
  // backfills.
  if (access.userType === UserType.ChamberAdmin) {
    return true;
  }

  if (access.userType !== UserType.ChamberStaff || route.ownerOnly) {
    return false;
  }

  if (route.allStaff) {
    return true;
  }

  if (route.requiresChamberPerksDashboardAccess) {
    return access.chamberPerksDashboardPermissions.includes(
      ChamberPerksAppAdminConsolePagePermissionsEnum.ChamberAdminAccessChamberDashboard
    );
  }

  if (route.anyPagePermissionsNeeded?.length) {
    return route.anyPagePermissionsNeeded.some((permission) =>
      access.chamberMemberProPermissions.includes(permission)
    );
  }

  return Boolean(
    route.pagePermissionNeeded &&
      access.chamberMemberProPermissions.includes(route.pagePermissionNeeded)
  );
};

/**
 * Monday 13390010292 (S10), Moeen Q6 Oct 28 2026: an employee without the
 * permission sees the Website Builder and Chamber Perks App™ Dashboard links
 * greyed out with the reason, not hidden. A chamber without the purchase
 * still hides them, and the owner always has them.
 */
export const isCmpExternalRoutePermissionLocked = ({
  route,
  access,
}: {
  route: ChamberAdminRoute;
  access: CmpRouteAccess;
}): boolean =>
  Boolean(route.isExternalLink) &&
  access.userType === UserType.ChamberStaff &&
  !route.ownerOnly &&
  !(route.requiresWebsiteBuilderEntitlement && !access.hasWebsiteBuilderEntitlement) &&
  !(route.requiresChamberPerksAppEntitlement && !access.hasChamberPerksAppEntitlement) &&
  !isCmpRouteAllowed({ route, access });

export const filterCmpRouteGroups = ({
  routeGroups,
  access,
}: {
  routeGroups: ChamberAdminRouteGroup[];
  access: CmpRouteAccess;
}): ChamberAdminRouteGroup[] =>
  routeGroups.reduce<ChamberAdminRouteGroup[]>((allowedGroups, routeGroup) => {
    if (routeGroup.nestedRoute) {
      const routes = routeGroup.nestedRoute.routes.filter((route) =>
        isCmpRouteAllowed({ route, access })
      );

      if (routes.length) {
        allowedGroups.push({
          nestedRoute: {
            ...routeGroup.nestedRoute,
            routes,
          },
        });
      }

      return allowedGroups;
    }

    if (
      routeGroup.route &&
      isCmpRouteAllowed({ route: routeGroup.route, access })
    ) {
      allowedGroups.push(routeGroup);
    }

    return allowedGroups;
  }, []);
